/**
 * <ChatStreamingMessage /> — an assistant turn (M22 / M23).
 *
 * While the turn is still streaming and no text has arrived yet, the
 * AI badge pulses ("thinking"). Once tokens arrive they stream into
 * the bubble. Tool calls show as "Running X…" chips; tool results
 * render via <InlineResult />, which picks the inline component by
 * the result's `render` kind — table / chart / card / link, plus
 * `draft` (M23) → <DraftRefinementCard />.
 */

import { AlertCircle, Info, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

import { AIBadge } from "../ui/AIBadge";
import { ChatMarkdown } from "./ChatMarkdown";
import { ConfirmationCard } from "./ConfirmationCard";
import { DraftRefinementCard } from "./DraftRefinementCard";
import { InlineCard } from "./InlineCard";
import { InlineChart } from "./InlineChart";
import { InlineLink } from "./InlineLink";
import { InlineTable } from "./InlineTable";
import { MessageActions } from "./MessageActions";
import { SuggestedFollowUps } from "./SuggestedFollowUps";
import { ThinkingDots } from "./ThinkingDots";
import type { ChatInlineResult, ChatTurn } from "./types";

type SaveDraftFn = (draft: {
  kind: string;
  title: string;
  content: string;
}) => void;

/** Pick the inline component for a tool result by its `render` kind. */
function InlineResult({
  result,
  onSendMessage,
  onSaveDraft,
}: {
  result: ChatInlineResult;
  onSendMessage?: (text: string) => void;
  onSaveDraft?: SaveDraftFn;
}) {
  if (result.render === "table") {
    return <InlineTable summary={result.summary} data={result.data} />;
  }
  if (result.render === "chart") {
    return <InlineChart summary={result.summary} data={result.data} />;
  }
  if (result.render === "card") {
    return <InlineCard summary={result.summary} data={result.data} />;
  }
  if (result.render === "link") {
    return <InlineLink summary={result.summary} data={result.data} />;
  }
  if (result.render === "draft") {
    return (
      <DraftRefinementCard
        summary={result.summary}
        data={result.data}
        onSendMessage={onSendMessage}
        onSaveDraft={onSaveDraft}
      />
    );
  }
  // "text" — plain summary.
  return (
    <div className="rounded-lg border border-border-subtle bg-surface-muted px-2.5 py-2 text-xs text-text-secondary">
      {result.summary}
    </div>
  );
}

export function ChatStreamingMessage({
  turn,
  sessionId,
  onSendMessage,
  onSaveDraft,
  onApplyConfirmation,
  onCancelConfirmation,
}: {
  turn: ChatTurn;
  /** Ephemeral chat session id, set by the orchestrator's first SSE
   *  event. Threaded down to MessageActions for feedback writes. */
  sessionId?: string | null;
  onSendMessage?: (text: string) => void;
  onSaveDraft?: SaveDraftFn;
  onApplyConfirmation?: (confirmationId: string) => void;
  onCancelConfirmation?: (confirmationId: string) => void;
}) {
  const { t } = useTranslation();
  // Show the persistent "working" signal whenever the turn is
  // streaming. The original `thinking` flag only fired when the
  // bubble was totally empty — but the user reports seeing no
  // animation between tool calls, which is exactly when Claude
  // is thinking the hardest (planning the next tool, then
  // synthesizing). Keep the sparkle pulsing for the whole turn.
  const isWorking = turn.streaming;
  // The bottom-of-bubble "Solvait is working…" three-dot line.
  // Renders whenever we're still streaming but haven't yet
  // produced text — i.e., either before any tool has finished,
  // OR between tool completion and synthesis. Hides the instant
  // text starts arriving (the streaming text is its own signal).
  const showWorkingDots = turn.streaming && !turn.text;

  return (
    <div className="flex flex-col gap-1.5 rounded-2xl border border-ai-subtle bg-ai-bg px-3 py-2.5">
      <div className="flex items-center gap-1.5">
        <AIBadge />
        <span className="text-xs font-semibold text-brand-blue">
          Solvait
        </span>
        {isWorking && (
          <Sparkles
            size={12}
            className="motion-safe:animate-pulse text-brand-teal"
            aria-label={t("app.cs_thinking")}
          />
        )}
      </div>

      {/* Tool-call indicators */}
      {turn.toolCalls.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {turn.toolCalls.map((name, i) => (
            <span
              key={`${name}-${i}`}
              className="inline-flex items-center gap-1 rounded bg-surface-muted px-1.5 py-0.5 text-xs text-text-secondary"
            >
              <Sparkles size={9} className="text-brand-teal" />
              {turn.streaming ? `Running ${name}…` : name}
            </span>
          ))}
        </div>
      )}

      {/* Tool results — rendered inline by `render` kind. */}
      {turn.results.map((r, i) => (
        <InlineResult
          key={`${r.name}-${i}`}
          result={r}
          onSendMessage={onSendMessage}
          onSaveDraft={onSaveDraft}
        />
      ))}

      {/* Parked mutations awaiting an Apply (M24). */}
      {turn.confirmations.map((c) => (
        <ConfirmationCard
          key={c.confirmationId}
          confirmation={c}
          onApply={() => onApplyConfirmation?.(c.confirmationId)}
          onCancel={() => onCancelConfirmation?.(c.confirmationId)}
        />
      ))}

      {/* Streamed assistant text — rendered as markdown so **bold**,
          lists, code, tables, links all format properly. */}
      {turn.text && <ChatMarkdown>{turn.text}</ChatMarkdown>}

      {/* Working indicator — three pulsing dots that fill the gap
          between tool completion and synthesis text. Without this
          the bubble appears frozen for the 1–10 seconds Claude
          spends planning the next call. Vanishes the instant the
          first text token arrives. */}
      {showWorkingDots && <ThinkingDots />}

      {/* Informational notices (cost cap reached, etc.) */}
      {turn.notices.map((n, i) => (
        <div
          key={`notice-${i}`}
          className="flex items-start gap-1.5 rounded-lg bg-surface-muted px-2.5 py-2 text-xs text-text-secondary"
        >
          <Info size={13} className="mt-px shrink-0" />
          <span>{n}</span>
        </div>
      ))}

      {/* Tool-level errors */}
      {turn.toolErrors.map((e, i) => (
        <div
          key={`err-${e.name}-${i}`}
          className="flex items-start gap-1.5 rounded-lg bg-amber-50 px-2.5 py-2 text-xs text-amber-800"
        >
          <AlertCircle size={13} className="mt-px shrink-0" />
          <span>
            <span className="font-medium">{e.name}</span>: {e.message}
          </span>
        </div>
      ))}

      {/* Turn-level error */}
      {turn.error && (
        <div className="flex items-start gap-1.5 rounded-lg bg-rose-50 px-2.5 py-2 text-xs text-rose-700">
          <AlertCircle size={13} className="mt-px shrink-0" />
          <span>{turn.error}</span>
        </div>
      )}

      {/* Suggested follow-ups (M25.5) — only once the turn settles. */}
      {!turn.streaming &&
        turn.followUps &&
        turn.followUps.length > 0 &&
        onSendMessage && (
          <SuggestedFollowUps
            followUps={turn.followUps}
            onPick={onSendMessage}
          />
        )}

      {/* Per-message actions (Copy + 👍 / 👎). Only render once the
          turn settles AND it actually has text — pure-tool turns
          with no synthesis text get no action row. */}
      {!turn.streaming && turn.text && (
        <MessageActions
          turnId={turn.id}
          sessionId={sessionId ?? null}
          text={turn.text}
        />
      )}
    </div>
  );
}
